import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { AppButton } from '@/components/common/atoms/AppButton';
import { ERROR_BOUNDARY_TEXT } from './errorBoundaryConstants';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, eventId: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    });
    this.setState({ eventId });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, eventId: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Box className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full p-8 text-center rounded-xl">
          <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {ERROR_BOUNDARY_TEXT.TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ERROR_BOUNDARY_TEXT.BODY}
            </Typography>
            {this.state.eventId && (
              <Typography variant="caption" color="text.disabled" className="font-mono">
                {ERROR_BOUNDARY_TEXT.REF_PREFIX} {this.state.eventId}
              </Typography>
            )}
            <Stack direction="row" spacing={2} className="mt-4">
              <AppButton variant="contained" onClick={this.handleReset}>
                {ERROR_BOUNDARY_TEXT.BUTTONS.RETRY}
              </AppButton>
              <AppButton
                variant="outlined"
                onClick={() => { window.location.href = '/dashboard'; }}
              >
                {ERROR_BOUNDARY_TEXT.BUTTONS.DASHBOARD}
              </AppButton>
            </Stack>
          </Stack>
        </Card>
      </Box>
    );
  }
}

export default ErrorBoundary;
